import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Building2, CheckCircle2, GraduationCap, LogOut, User } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const papelLabels = { professor: 'Professor', aluno: 'Aluno' }

function ConvitePage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { usuario, setSession, adicionarInstituicao, logout } = useAuthStore()
  const [form, setForm] = useState({ nome: '', senha: '' })

  const conviteQuery = useQuery({
    queryKey: ['convite-instituicao', token],
    queryFn: async () => (await api.get(`/instituicoes/convites/${token}`)).data
  })

  const cadastrar = useMutation({
    mutationFn: () => api.post(`/instituicoes/convites/${token}/cadastrar`, form),
    onSuccess: response => {
      setSession(response.data)
      toast.success(response.data.message)
      navigate('/instituicao')
    },
    onError: error => toast.error(getApiError(error))
  })

  const aceitar = useMutation({
    mutationFn: () => api.post(`/instituicoes/convites/${token}/aceitar`),
    onSuccess: response => {
      adicionarInstituicao(response.data.vinculo)
      toast.success(response.data.message)
      navigate('/instituicao')
    },
    onError: error => toast.error(getApiError(error))
  })

  const convite = conviteQuery.data?.convite
  const emailCorreto = usuario?.email?.toLowerCase() === convite?.email?.toLowerCase()

  return (
    <div className="min-h-screen bg-white px-6 py-10 text-gray-900 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-lg flex-col items-center justify-center">
        <Link to="/" className="mb-6" aria-label="Agente de Estudo — ir para o início">
          <img src="/brand/agente-de-estudo-icone.svg" alt="" width="48" height="48" className="h-12 w-12" />
        </Link>

        {conviteQuery.isLoading && <p className="card w-full text-center text-sm text-gray-500">Carregando convite...</p>}
        {conviteQuery.isError && <div className="card w-full text-center"><p className="font-medium">Convite indisponível</p><p className="mt-2 text-sm text-gray-500">{getApiError(conviteQuery.error)}</p><Link to="/" className="btn-primary mt-5 inline-block">Ir para o início</Link></div>}

        {convite && (
          <div className="w-full space-y-5">
            <div className="text-center"><p className="text-sm text-gray-500">Você foi convidado para</p><h1 className="mt-1 text-2xl font-medium">{convite.instituicao.nome}</h1></div>

            <div className="card space-y-3">
              <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800"><Building2 size={19} /></span><div><p className="text-xs text-gray-500">Instituição</p><p className="text-sm font-medium">{convite.instituicao.nome}</p></div></div>
              <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800"><User size={19} /></span><div><p className="text-xs text-gray-500">Participação</p><p className="text-sm font-medium">{papelLabels[convite.papel]}</p></div></div>
              {convite.turma && <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800"><GraduationCap size={19} /></span><div><p className="text-xs text-gray-500">Turma</p><p className="text-sm font-medium">{convite.turma.nome} · {convite.turma.ano_letivo}</p></div></div>}
            </div>

            {usuario && emailCorreto && (
              <div className="card text-center"><CheckCircle2 size={26} className="mx-auto mb-3 text-emerald-600" /><p className="font-medium">Aceitar com sua conta atual</p><p className="mt-1 text-sm text-gray-500">{usuario.email}</p><button type="button" onClick={() => aceitar.mutate()} disabled={aceitar.isLoading} className="btn-primary mt-5 w-full">{aceitar.isLoading ? 'Aceitando...' : 'Aceitar convite'}</button></div>
            )}

            {usuario && !emailCorreto && (
              <div className="card text-center"><p className="font-medium">Entre com o e-mail convidado</p><p className="mt-2 text-sm text-gray-500">Este convite pertence a <strong>{convite.email}</strong>, mas você está usando {usuario.email}.</p><button type="button" onClick={logout} className="btn-secondary mt-5 inline-flex items-center gap-2"><LogOut size={15} /> Sair desta conta</button></div>
            )}

            {!usuario && (
              <div className="card">
                <h2 className="font-medium">Criar conta e aceitar</h2>
                <p className="mt-1 text-sm text-gray-500">O convite está vinculado ao e-mail {convite.email}.</p>
                <form className="mt-5 space-y-3" autoComplete="off" onSubmit={event => { event.preventDefault(); cadastrar.mutate() }}>
                  <input className="input-field" type="text" name="nome-convidado" autoComplete="name" placeholder="Nome completo" value={form.nome} onChange={event => setForm({ ...form, nome: event.target.value })} required />
                  <input className="input-field" type="password" name="nova-senha-convidado" autoComplete="new-password" placeholder="Crie uma senha (mín. 6 caracteres)" minLength={6} value={form.senha} onChange={event => setForm({ ...form, senha: event.target.value })} required />
                  <button className="btn-primary w-full" disabled={cadastrar.isLoading}>{cadastrar.isLoading ? 'Criando conta...' : 'Criar conta e aceitar'}</button>
                </form>
                <p className="mt-4 text-center text-sm text-gray-500">Já possui conta com este e-mail? <Link className="font-medium text-black underline dark:text-white" to={`/login?next=${encodeURIComponent(`/convite/${token}`)}`}>Entrar</Link></p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ConvitePage
